/**
 * IAM group hooks — list/create/detail/delete, policy attach/detach and member
 * add/remove. Scoped by `serviceId`; membership changes invalidate the group
 * detail so the members table reflects the server immediately.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { serviceApiV3, unwrap, s3Keys } from './_shared';
import { iamBase, iamPath, type AttachedPolicy } from './iam-shared';

/** One IAM group as listed (`GET /iam/groups`). */
export interface IamGroup {
  group_name: string;
  group_id?: string;
  arn?: string;
  create_date?: string | null;
}

/** A user that belongs to a group (`members` on the group detail). */
export interface GroupMember {
  user_name: string;
  user_id?: string;
  arn?: string;
}

/** Full group with members and attached policies (`GET /iam/groups/:name`). */
export interface IamGroupDetail {
  group: IamGroup;
  members: GroupMember[];
  attached_policies: AttachedPolicy[];
}

/** List the service's IAM groups. */
export function useIamGroups(serviceId: string | undefined) {
  return useQuery<IamGroup[]>({
    queryKey: s3Keys.iamGroups(serviceId ?? ''),
    enabled: !!serviceId,
    staleTime: 30_000,
    queryFn: async () => {
      const res = await serviceApiV3.get(`${iamBase(serviceId!)}/groups`);
      return unwrap<{ groups?: IamGroup[] }>(res)?.groups ?? [];
    },
  });
}

/** Fetch one group with its members and attached policies. */
export function useIamGroup(serviceId: string | undefined, name: string | undefined) {
  return useQuery<IamGroupDetail>({
    queryKey: s3Keys.iamGroup(serviceId ?? '', name ?? ''),
    enabled: !!serviceId && !!name,
    queryFn: async () =>
      unwrap<IamGroupDetail>(await serviceApiV3.get(iamPath(serviceId!, 'groups', name!))),
  });
}

/** Create an IAM group by name. */
export function useCreateIamGroup(serviceId: string) {
  const qc = useQueryClient();
  return useMutation<IamGroup, Error, string>({
    mutationFn: async (groupName) =>
      unwrap<IamGroup>(
        await serviceApiV3.post(`${iamBase(serviceId)}/groups`, { group_name: groupName }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: s3Keys.iamGroups(serviceId) }),
  });
}

/** Delete a group — its member users keep existing, only the grouping is removed. */
export function useDeleteIamGroup(serviceId: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: async (name) => {
      await serviceApiV3.delete(iamPath(serviceId, 'groups', name));
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: s3Keys.iamGroups(serviceId) }),
  });
}

/** Attach or detach a managed policy on a group (`attach` toggles verb). */
export function useGroupPolicyMutation(serviceId: string, attach: boolean) {
  const qc = useQueryClient();
  return useMutation<void, Error, { name: string; policyArn: string }>({
    mutationFn: async ({ name, policyArn }) => {
      const verb = attach ? 'attach-policy' : 'detach-policy';
      await serviceApiV3.post(iamPath(serviceId, 'groups', name, `/${verb}`), {
        policy_arn: policyArn,
      });
    },
    onSuccess: (_r, { name }) =>
      qc.invalidateQueries({ queryKey: s3Keys.iamGroup(serviceId, name) }),
  });
}

/** Add or remove a user from a group (`add` toggles verb). */
export function useGroupMemberMutation(serviceId: string, add: boolean) {
  const qc = useQueryClient();
  return useMutation<void, Error, { name: string; userName: string }>({
    mutationFn: async ({ name, userName }) => {
      const verb = add ? 'add-user' : 'remove-user';
      await serviceApiV3.post(iamPath(serviceId, 'groups', name, `/${verb}`), {
        user_name: userName,
      });
    },
    onSuccess: (_r, { name }) =>
      qc.invalidateQueries({ queryKey: s3Keys.iamGroup(serviceId, name) }),
  });
}
